import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { TypedMemo } from '../../../common/types';
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton';
import { usePlaylistFollowersTimeSeries } from '../../hooks/usePlaylistFollowersTimeSeries';
import { useChartTokens } from '../../theme/useChartTokens';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

interface PlaylistFollowersChartProps {
  playlistId: string;
  endDate?: Date;
  rangeDays?: number;
}

const formatFollowersValue = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

const PlaylistFollowersChartComponent: React.FC<PlaylistFollowersChartProps> = ({
  playlistId,
  endDate,
  rangeDays = 30,
}) => {
  const intl = useIntl();
  const chart = useChartTokens();
  const {
    data: timeSeriesData,
    isLoading,
    error,
  } = usePlaylistFollowersTimeSeries({ playlistId, days: rangeDays, endDate });

  const data = useMemo(() => timeSeriesData?.data ?? [], [timeSeriesData?.data]);

  const dateRangeText = useMemo(() => {
    if (data.length < 2) {
      return '';
    }
    const startFormatted = new Date(data[0]!.date).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
    });
    const endFormatted = new Date(data[data.length - 1]!.date).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startFormatted} - ${endFormatted}`;
  }, [data, intl.locale]);

  const yAxisDomain = useMemo(() => {
    if (data.length === 0) {
      return ['auto', 'auto'];
    }
    const counts = data.map((d) => d.count);
    const minCount = Math.min(...counts);
    const maxCount = Math.max(...counts);
    const padding = Math.max(1, (maxCount - minCount) * 0.1);
    return [Math.max(0, Math.floor(minCount - padding)), Math.ceil(maxCount + padding)];
  }, [data]);

  const formatXAxisDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateForTooltip = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const title = intl.formatMessage({
    id: 'app.sml.playlists.followers-chart.title',
    defaultMessage: 'Followers Over Time',
  });

  return (
    <Panel style={{ minHeight: '318px' }}>
      <PanelHead title={title} subtitle={dateRangeText} />
      <PanelBody>
        {isLoading ? (
          <div style={{ height: '250px' }}>
            <ChartSkeleton />
          </div>
        ) : error !== null && error !== undefined ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '250px', color: chart.tx2 }}
          >
            <div className="text-center">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mb-2"
                style={{ fontSize: '2rem', color: chart.neg }}
              />
              <div style={{ fontSize: '14px' }}>
                {intl.formatMessage({
                  id: 'app.sml.chart.error',
                  defaultMessage: 'Error loading chart data',
                })}
              </div>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '250px', color: chart.tx2 }}
          >
            <div className="text-center">
              <FontAwesomeIcon icon={faChartLine} className="mb-2" style={{ fontSize: '2rem' }} />
              <div style={{ fontSize: '14px' }}>
                {intl.formatMessage({
                  id: 'app.sml.chart.no-data',
                  defaultMessage: 'No data available for this period',
                })}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data}
                margin={{
                  top: 10,
                  right: 10,
                  left: 10,
                  bottom: 10,
                }}
              >
                <defs>
                  <linearGradient id="followersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chart.brand} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={chart.brand} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: chart.label }}
                  tickFormatter={formatXAxisDate}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                />
                <YAxis
                  domain={yAxisDomain}
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: chart.axis }}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                  tickFormatter={formatFollowersValue}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: chart.tooltipBg,
                    border: `1px solid ${chart.line}`,
                    borderRadius: '8px',
                    fontSize: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    color: chart.tooltipTx,
                  }}
                  labelFormatter={formatDateForTooltip}
                  formatter={(value: number) => [value.toLocaleString(), 'Followers']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={chart.brandLift}
                  strokeWidth={2}
                  fill="url(#followersGradient)"
                  dot={{
                    fill: chart.brandLift,
                    strokeWidth: 1,
                    r: 3,
                  }}
                  activeDot={{
                    r: 5,
                    stroke: chart.brandLift,
                    strokeWidth: 1,
                    fill: chart.dotCore,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const PlaylistFollowersChart = TypedMemo(PlaylistFollowersChartComponent);
